import { useRecoilValue } from 'recoil';

import { contentDetailOnDemandAtom } from '@/state/atom/translationAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import CorporationUtils from '@/common/utils/CorporationUtils';
import TranslationConstant from '@/common/constant/TranslationConstant';
import Constant from '@/common/constant/Constant';

const { ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

/**
 * Resolve the value a detail field should DISPLAY for on-demand content
 * translation, without touching the form value.
 *
 * When the 「翻訳版」toggle is ON (activeView = TRANSLATION) and the field has a
 * translated value, the hook reports the translation and flags
 * `isShowingTranslation`; otherwise it reports the original value passed in. The
 * caller decides how to render (e.g. a read-only branch when showing the
 * translation) so the edit form value is never polluted by the translation.
 *
 * @param {*} originalValue - The value the field would normally display.
 * @param {{ contentId: string, fieldKey: string }} params
 *   - contentId: the ticket/content record key (detailTicket.id).
 *   - fieldKey: the field key inside the record (fieldId).
 * @returns {{ displayValue: *, isShowingTranslation: boolean }}
 */
const useOnDemandContentDisplay = (
    originalValue,
    { contentId, fieldKey } = {},
    displayMode = Constant.DISPLAY_MODE.VIEW,
) => {
    const contentDetailOnDemand = useRecoilValue(contentDetailOnDemandAtom);
    const corporationDetail = useRecoilValue(corporationAtom);
    const record = contentId ? contentDetailOnDemand[contentId] : null;

    // Org multi-language off -> never show a cached/in-memory translation, even if the
    // record is still TRANSLATED from before the setting was turned off.
    const isMultiLanguageOn = CorporationUtils.isEnableSettingMultiLanguage(corporationDetail);
    const isTranslationView = record?.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;
    const translated = record?.fields?.[fieldKey]?.translated;

    const isShowingTranslation = Boolean(
        isMultiLanguageOn &&
        displayMode === Constant.DISPLAY_MODE.VIEW &&
        isTranslationView &&
        translated != null
    );

    return {
        displayValue: isShowingTranslation ? translated : originalValue,
        isShowingTranslation
    };
};

export default useOnDemandContentDisplay;
